import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiLock, FiShield, FiAlertCircle, FiClock } = FiIcons;

// Base64 von "Weativ2021!" ist "V2VhdGl2MjAyMSE="
const ENCODED_PASS = "V2VhdGl2MjAyMSE=";

export default function AccessGuard({ children }) {
  const [password, setPassword] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockTimer, setLockTimer] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let interval;
    if (lockTimer > 0) {
      interval = setInterval(() => {
        setLockTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [lockTimer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (lockTimer > 0) return;

    if (btoa(password) === ENCODED_PASS) {
      setIsAuthorized(true);
      setError('');
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPassword('');

      if (newAttempts % 3 === 0) {
        const waitTime = (newAttempts / 3) * 5;
        setLockTimer(waitTime);
        setError(`Zu viele Fehlversuche. Bitte warten Sie ${waitTime} Sekunden.`);
      } else {
        setError('Falsches Passwort. Bitte versuchen Sie es erneut.');
      }
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Hauptinhalt der App */}
      <div className={!isAuthorized ? "filter blur-xl pointer-events-none select-none" : ""}>
        {children}
      </div>

      {/* Sperrbildschirm Overlay */}
      <AnimatePresence>
        {!isAuthorized && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20"
            >
              <div className="flex flex-col items-center text-center mb-8">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 mb-4">
                  <SafeIcon icon={FiLock} className="text-3xl text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Zugriff geschützt</h2>
                <p className="text-slate-500 mt-2">Bitte geben Sie das Passwort ein, um die Weativ Gutschrift-Verwaltung zu nutzen.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <input
                    type="password"
                    autoFocus
                    disabled={lockTimer > 0}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Passwort eingeben..."
                    className={`w-full px-5 py-4 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50 transition-all ${
                      lockTimer > 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                    <SafeIcon icon={FiShield} />
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex items-center gap-2 p-3 rounded-lg text-sm font-medium ${
                      lockTimer > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'
                    }`}
                  >
                    <SafeIcon icon={lockTimer > 0 ? FiClock : FiAlertCircle} />
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={lockTimer > 0 || !password}
                  className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {lockTimer > 0 ? (
                    <>
                      <span className="animate-pulse">Sperre aktiv ({lockTimer}s)</span>
                    </>
                  ) : (
                    'Anmelden'
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-400">
                  © {new Date().getFullYear()} Weativ GmbH • Alle Rechte vorbehalten
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}